import React from 'react';
import PropTypes from 'prop-types';
import _AccordionItem from './_AccordionItem.react';

/**
 * A layout component for displaying a collapsible accordion item that can be
 * used to group and organize content.
 *
 * WARNING: This is an alpha version of a component. Breaking changes may be
 * made without respecting semantic versioning. Use at your own risk.
 *
 * **Example Usage**
 * ddk._Accordion(
 *     control_groups=[
 *         {
 *             "title": "Model Parameters",
 *             "children": [
 *                 dcc.Slider(
 *                     name="n", min=0, max=10, marks={0: "0", 5: "5", 10: "10"}, value=5
 *                 ),
 *             ],
 *         },
 *         {
 *             "title": "Plot Axes",
 *             "children": [
 *                 dcc.Dropdown(
 *                     name="x-axis",
 *                     placeholder="City",
 *                     options=[
 *                         {"label": "New York City", "value": "NYC"},
 *                         {"label": "Montreal", "value": "MTL"},
 *                     ],
 *                     value="MTL",
 *                 ),
 *             ],
 *         },
 *     ]
 * )
 * ```
 */
export default function _Accordion({control_groups, action_button}) {
    return (
        <>
            <div className={`accordion ${action_button ? "accordion_with_action" : "accordion_without_action"}`}>
                {control_groups.map(({title, description, id, children}) => (
                    <_AccordionItem
                        title={title}
                        description={description}
                        key={id}
                    >
                        {children}
                    </_AccordionItem>
                ))}
            </div>
            {action_button && <div className="action_button_container">{action_button}</div>}
        </>
    );
}

_Accordion.propTypes = {
    /**
     * Each control group will make up one section of the accordion (one AccordionItem).
     * The children of that group will be placed inside that section/AccordionItem.
     */
    control_groups: PropTypes.arrayOf(
        PropTypes.shape({
            /**
             * The title of the accordion section.
             */
            title: PropTypes.string.isRequired,
            /**
             * The description of the accordion section.
             */
            description: PropTypes.string.isRequired,
            /**
             * The id of the accordion section.
             */
            id: PropTypes.string.isRequired,
            /**
             * The elements to be placed in this group accordion section/AccordionItem.
             */
            children: PropTypes.arrayOf(PropTypes.node).isRequired,
        })
    ).isRequired,
    /**
     * The action button to be placed at the bottom of the accordian.
     */
    action_button: PropTypes.node,
};
